import { useState } from 'react';
import { Modal } from './Modal';

/**
 * Peer agreement at pairing start — three short bullets, one button.
 *
 * Surfaces the BeFriender-style principles (listen-not-advise, keep-private,
 * refer-to-988-in-crisis) before the user can post their first message.
 * The wording is the contract; bumping PAIRING_AGREEMENT_VERSION on the
 * server re-opens this modal for everyone.
 */
export function PeerAgreementModal({
  onAgree,
  onCancel,
}: {
  onAgree: () => Promise<void>;
  onCancel: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      await onAgree();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record your agreement.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Before you send your first message" onClose={onCancel}>
      <p className="muted">
        LessAlone works because peers honour a few simple commitments. Take a moment with these.
      </p>
      <ul className="agreement-list">
        <li>
          <span className="agreement-eyebrow">Listen, don't advise.</span>
          <span>People often need to be heard more than fixed.</span>
        </li>
        <li>
          <span className="agreement-eyebrow">Keep what we talk about private.</span>
          <span>What's shared here stays here.</span>
        </li>
        <li>
          <span className="agreement-eyebrow">If a crisis comes up, point them to 988.</span>
          <span>You aren't the crisis line — and that's okay.</span>
        </li>
      </ul>
      {error && <p className="error">{error}</p>}
      <div className="modal-actions">
        <button type="button" className="secondary" onClick={onCancel} disabled={busy}>
          Not yet
        </button>
        <button type="button" onClick={accept} disabled={busy}>
          {busy ? 'Saving…' : 'I agree'}
        </button>
      </div>
    </Modal>
  );
}
